import React, { useState, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Lock, ChevronLeft, ChevronRight, X, Loader2, Pencil, Trash2, Check } from 'lucide-react';
import { searchPlaces } from '../services/apiClient';
import { getBackendUrl } from '../config/api';
import { apiErrorText, birthTimeError } from '../utils/birthTime';

/* ─── Cosmic star field for ProfileSwitcher ─────────────────────────────────── */
const PS_STARS = Array.from({ length: 38 }, (_, i) => ({
  cx: `${((i * 41 + 7)  % 97).toFixed(1)}%`,
  cy: `${((i * 67 + 23) % 94).toFixed(1)}%`,
  r:  i % 8 === 0 ? 1.7 : i % 4 === 0 ? 1.0 : 0.50,
  opacity: +(0.18 + (i % 7) * 0.08).toFixed(2),
  fill: i % 5 === 0 ? '#ffffff' : i % 3 === 0 ? '#FFD966' : '#DDD6FE',
  twinkle: i % 3 === 0,
  dur: `${1.3 + (i % 5) * 0.4}s`,
}));

const RELATION_COLORS = {
  Spouse:  'from-rose-400 to-pink-600',
  Child:   'from-sky-400 to-cyan-500',
  Parent:  'from-amber-400 to-orange-500',
  Sibling: 'from-emerald-400 to-teal-500',
  Friend:  'from-violet-400 to-purple-600',
  Other:   'from-amber-500/70 to-yellow-600/70',
};

const RELATION_LABEL_COLORS = {
  Spouse:  'text-rose-300',
  Child:   'text-sky-300',
  Parent:  'text-amber-300',
  Sibling: 'text-emerald-300',
  Friend:  'text-violet-300',
  Other:   'text-amber-200',
};

// ─── API helpers ─────────────────────────────────────────────────────────────
const getToken = () => localStorage.getItem('jyotish_session_token');
const profilesApi = async (method, path, body) => {
  const res = await fetch(`${getBackendUrl()}${path}`, {
    method,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
    body: body ? JSON.stringify(body) : undefined,
  });
  return res.json();
};

// ─── Shared Family Member Form (used in ProfileSwitcher inline & AddFamilyMemberModal) ───
export const FamilyMemberForm = ({
  editingProfile = null,
  onSave,
  onCancel,
}) => {
  const isEdit = !!editingProfile;
  const [form, setForm] = useState(() => {
    if (editingProfile) {
      let dobInput = '';
      if (editingProfile.dob) {
        const parts = editingProfile.dob.split('-');
        dobInput = parts.length === 3 && parts[0].length === 2
          ? `${parts[2]}-${parts[1]}-${parts[0]}`
          : editingProfile.dob;
      }
      return {
        name: editingProfile.name || '',
        relation_type: editingProfile.relation_type || 'Spouse',
        gender: editingProfile.gender || 'other',
        dob: dobInput,
        tob: editingProfile.tob || '',
        pob: editingProfile.pob || '',
      };
    }
    return { name: '', relation_type: 'Spouse', gender: 'other', dob: '', tob: '', pob: '' };
  });

  const [placeSuggestions, setPlaceSuggestions] = useState([]);
  const [loadingPlace, setLoadingPlace] = useState(false);
  const [selectedPlace, setSelectedPlace] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const searchDebounceRef = useRef(null);

  const handlePlaceInput = useCallback(async (val) => {
    setForm(f => ({ ...f, pob: val }));
    setSelectedPlace(null);
    clearTimeout(searchDebounceRef.current);
    if (val.length < 3) { setPlaceSuggestions([]); return; }
    searchDebounceRef.current = setTimeout(async () => {
      setLoadingPlace(true);
      try {
        const res = await searchPlaces(val);
        setPlaceSuggestions(res?.suggestions || []);
      } catch { setPlaceSuggestions([]); }
      finally { setLoadingPlace(false); }
    }, 350);
  }, []);

  // BIRTH-TIME-1: the server 422s an impossible time; say so inline and keep Save off.
  const tobError = birthTimeError(form.tob);
  const canSave = !!form.name && !!form.dob && !tobError;

  const handleSubmit = async () => {
    if (!canSave) return;
    setSaving(true);
    setSaveError(null);
    try {
      const token = getToken();
      if (!token) { setSaveError('Sign in to add family members.'); return; }
      const [y, m, d] = form.dob.split('-');
      const formattedDob = `${d}-${m}-${y}`;
      const payload = { ...form, dob: formattedDob };
      if (selectedPlace) { payload.lat = selectedPlace.latitude; payload.lon = selectedPlace.longitude; }

      if (isEdit) {
        const data = await profilesApi('PUT', `/api/profiles/${editingProfile.profile_id}`, payload);
        if (data.success) {
          onSave?.(data.profile);
        } else {
          setSaveError(apiErrorText(data, 'Failed to update.'));
        }
      } else {
        const res = await fetch(`${getBackendUrl()}/api/profiles`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify(payload),
        });
        const data = await res.json();
        if (data.success) {
          onSave?.(data.profile);
        } else {
          setSaveError(apiErrorText(data, 'Failed to save.'));
        }
      }
    } catch {
      setSaveError('Network error. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="rounded-xl border border-amber-500/20 bg-[#0d0920]/90 p-3.5 space-y-2.5 backdrop-blur-xl"
      style={{ boxShadow: '0 0 18px rgba(245,158,11,0.06)' }}
    >
      <p className="text-[9px] font-semibold tracking-widest uppercase"
        style={{ color: '#F59E0B' }}>
        {isEdit ? `Edit — ${editingProfile?.name}` : 'Add Family Member'}
      </p>
      <input type="text" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Full name"
        className="w-full bg-white/5 border border-amber-500/15 rounded-lg px-3 py-2 text-white text-xs placeholder-gray-600 focus:outline-none focus:border-amber-500/50"
        data-testid={isEdit ? 'edit-member-name' : 'inline-member-name'} />
      <select value={form.relation_type} onChange={e => setForm(f => ({ ...f, relation_type: e.target.value }))}
        className="w-full bg-white/5 border border-amber-500/15 rounded-lg px-3 py-2 text-white text-xs focus:outline-none focus:border-amber-500/50"
        data-testid={isEdit ? 'edit-member-relation' : 'inline-member-relation'}>
        {['Spouse', 'Child', 'Parent', 'Sibling', 'Friend', 'Other'].map(r => (
          <option key={r} value={r} className="bg-[color:var(--bg-base)]">{r}</option>
        ))}
      </select>
      <div>
        <p className="text-[9px] text-gray-500 mb-1.5">Gender</p>
        <div className="flex gap-1.5">
          {[['male', 'Male'], ['female', 'Female'], ['other', 'Other']].map(([val, lbl]) => (
            <button key={val} type="button" onClick={() => setForm(f => ({ ...f, gender: val }))}
              className={`flex-1 py-1.5 rounded-lg text-[10px] font-medium border transition-all ${
                form.gender === val ? 'bg-amber-500/20 border-amber-500/50 text-amber-300' : 'bg-white/5 border-white/10 text-gray-500'
              }`}
              data-testid={`${isEdit ? 'edit' : 'inline'}-gender-${val}`}>{lbl}</button>
          ))}
        </div>
      </div>
      <div className="flex gap-2">
        <input type="date" value={form.dob} onChange={e => setForm(f => ({ ...f, dob: e.target.value }))}
          className="flex-1 bg-white/5 border border-amber-500/15 rounded-lg px-2 py-2 text-white text-xs focus:outline-none focus:border-amber-500/50"
          data-testid={isEdit ? 'edit-member-dob' : 'inline-member-dob'} />
        <input type="time" value={form.tob} onChange={e => setForm(f => ({ ...f, tob: e.target.value }))}
          aria-invalid={!!(form.tob && tobError)}
          className={`flex-1 bg-white/5 border rounded-lg px-2 py-2 text-white text-xs focus:outline-none focus:border-amber-500/50 ${form.tob && tobError ? 'border-red-500/60' : 'border-amber-500/15'}`}
          data-testid={isEdit ? 'edit-member-tob' : 'inline-member-tob'} />
      </div>
      {tobError && (form.tob || form.dob) && (
        <p className="text-red-400 text-[10px]" data-testid={isEdit ? 'edit-member-tob-error' : 'inline-member-tob-error'}>
          {form.tob ? tobError : 'Enter the birth time.'}
        </p>
      )}
      <div className="relative">
        <input type="text" value={form.pob} onChange={e => handlePlaceInput(e.target.value)} placeholder="Place of birth"
          className="w-full bg-white/5 border border-amber-500/15 rounded-lg px-3 py-2 text-white text-xs placeholder-gray-600 focus:outline-none focus:border-amber-500/50"
          data-testid={isEdit ? 'edit-member-pob' : 'inline-member-pob'} />
        {loadingPlace && <Loader2 className="absolute right-2.5 top-2.5 w-3 h-3 text-gray-500 animate-spin" />}
        {placeSuggestions.length > 0 && (
          <div className="absolute z-50 left-0 right-0 top-full mt-1 bg-[color:var(--bg-base)] border border-white/15 rounded-lg overflow-hidden shadow-xl max-h-32 overflow-y-auto">
            {placeSuggestions.slice(0, 4).map((s, i) => (
              <button key={i} onClick={() => { setForm(f => ({ ...f, pob: s.full_name || s.name || s })); setSelectedPlace({ latitude: s.latitude, longitude: s.longitude }); setPlaceSuggestions([]); }}
                className="w-full text-left px-3 py-2 text-xs text-gray-300 hover:bg-white/10 transition-colors border-b border-white/5 last:border-0">
                {s.label || s.display_name || s.full_name || s.name || s}
              </button>
            ))}
          </div>
        )}
      </div>
      {saveError && <p className="text-red-400 text-[10px] bg-red-900/20 border border-red-500/30 rounded px-2.5 py-1.5">{saveError}</p>}
      <div className="flex gap-2">
        <button onClick={handleSubmit}
          disabled={saving || !canSave}
          className="flex-1 py-2 text-white text-xs font-semibold rounded-lg disabled:opacity-50 flex items-center justify-center gap-1.5 transition-all"
          style={{ background: 'linear-gradient(135deg, #F59E0B, #D97706)' }}
          data-testid={isEdit ? 'save-edit-member-btn' : 'inline-save-member-btn'}>
          {saving ? <><Loader2 className="w-3 h-3 animate-spin" /> Saving…</> : isEdit ? <><Check className="w-3 h-3" /> Update</> : 'Save Member'}
        </button>
        <button onClick={onCancel}
          className="px-3 py-2 bg-white/8 text-gray-400 text-xs rounded-lg border border-white/10">
          Cancel
        </button>
      </div>
    </div>
  );
};

export const AddFamilyMemberModal = ({ isOpen, onClose, onAdd }) => {
  if (!isOpen) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/75 backdrop-blur-sm overflow-y-auto"
      data-testid="add-family-member-modal"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md my-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="add-member-inline-panel"
      >
        <FamilyMemberForm
          onSave={(newProfile) => {
            onAdd?.(newProfile);
          }}
          onCancel={onClose}
        />
      </div>
    </div>
  );
};

const Avatar = ({ label, gradient, isActive, isSelf, onClick, size = 48, editMode, onDelete, labelColorClass }) => {
  const initial = (label || '?')[0].toUpperCase();
  return (
    <motion.div
      className="flex flex-col items-center gap-1.5 flex-shrink-0 focus:outline-none relative"
      data-testid={`profile-switcher-${isSelf ? 'me' : label?.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <motion.button
        whileHover={{ scale: editMode ? 1 : 1.08 }}
        whileTap={{ scale: 0.94 }}
        onClick={onClick}
        className="focus:outline-none relative"
        style={{ width: size + 7, height: size + 7 }}
      >
        {/* Ring wrapper */}
        <div
          className={`rounded-full p-[2.5px] transition-all duration-200 w-full h-full ${
            isActive && !editMode
              ? 'bg-gradient-to-br from-amber-400 via-yellow-300 to-orange-400 shadow-[0_0_14px_rgba(251,191,36,0.6)]'
              : editMode
              ? 'bg-gradient-to-br from-amber-500/30 to-orange-500/30 ring-1 ring-amber-500/40'
              : 'bg-transparent'
          }`}
        >
          <div
            className={`w-full h-full rounded-full bg-gradient-to-br ${gradient} flex items-center justify-center text-white font-bold relative`}
            style={{ fontSize: size * 0.35 }}
          >
            {initial}
            {isActive && !editMode && (
              <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-emerald-400 border-[1.5px] border-[#0d0b24]" />
            )}
            {editMode && (
              <div className="absolute inset-0 rounded-full bg-black/40 flex items-center justify-center">
                <Pencil className="w-3.5 h-3.5 text-amber-300" />
              </div>
            )}
          </div>
        </div>
      </motion.button>

      {/* Delete X badge in edit mode */}
      {editMode && onDelete && (
        <motion.button
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          exit={{ scale: 0 }}
          whileTap={{ scale: 0.85 }}
          onClick={(e) => { e.stopPropagation(); onDelete(); }}
          className="absolute -top-0.5 -right-0.5 w-4.5 h-4.5 rounded-full bg-red-500 border border-[#0d0b24] flex items-center justify-center z-10"
          style={{ width: 18, height: 18 }}
          data-testid={`delete-profile-${label?.toLowerCase().replace(/\s+/g, '-')}`}
        >
          <X className="w-2.5 h-2.5 text-white" />
        </motion.button>
      )}

      <span
        className={`text-[9px] font-semibold truncate transition-colors duration-200 ${
          isActive && !editMode
            ? 'text-amber-400'
            : editMode
            ? 'text-amber-300/70'
            : labelColorClass || 'text-white/65'
        }`}
        style={{ maxWidth: size + 7 }}
      >
        {label}
      </span>
    </motion.div>
  );
};

const ProfileSwitcher = ({
  userInfo,
  familyProfiles = [],
  activeProfile,        // null = self, otherwise profile object
  onSwitch,             // (profile | null) => void
  onAdd,                // called after successful member creation (profile) => void
  onDelete,             // called after member deleted (profile_id) => void
  onUpdate,             // called after member updated (profile) => void
  isLocked = false,
  compact = false,
  mobileStrip = false,
}) => {
  const scrollRef = useRef(null);
  const [showLeft, setShowLeft] = useState(false);
  const [showRight, setShowRight] = useState(false);
  const [showAddPanel, setShowAddPanel] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editingProfile, setEditingProfile] = useState(null); // profile being edited
  const [deletingId, setDeletingId] = useState(null);

  const openEditPanel = (profile) => {
    setEditingProfile(profile);
    setShowAddPanel(false);
  };

  const handleDeleteMember = async (profileId) => {
    setDeletingId(profileId);
    try {
      await profilesApi('DELETE', `/api/profiles/${profileId}`);
      onDelete?.(profileId);
      if (editingProfile?.profile_id === profileId) setEditingProfile(null);
    } catch {}
    finally { setDeletingId(null); }
  };

  const closeEditMode = () => {
    setEditMode(false);
    setEditingProfile(null);
  };

  const checkScroll = () => {
    if (!scrollRef.current) return;
    const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
    setShowLeft(scrollLeft > 4);
    setShowRight(scrollLeft < scrollWidth - clientWidth - 4);
  };

  const scroll = (dir) => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollBy({ left: dir * 80, behavior: 'smooth' });
  };

  const isSelf = activeProfile === null;
  const selfGradient = 'from-indigo-500 to-purple-600';

  // ── Mobile strip mode — bare stories row, no card container ──────────────
  if (mobileStrip) {
    return (
      <div data-testid="profile-switcher-container" className="relative">

        {/* Scrollable avatar row with right-edge fade */}
        <div
          ref={scrollRef}
          onScroll={checkScroll}
          className="flex gap-3.5 overflow-x-auto pb-1.5 px-4"
          style={{
            scrollbarWidth: 'none',
            WebkitOverflowScrolling: 'touch',
            maskImage: 'linear-gradient(to right, black 88%, transparent 100%)',
            WebkitMaskImage: 'linear-gradient(to right, black 88%, transparent 100%)',
          }}
        >
          {/* Me */}
          <Avatar
            label="Me"
            gradient={isSelf ? 'from-amber-400 to-orange-500' : selfGradient}
            isActive={isSelf}
            isSelf
            onClick={() => { if (!editMode) onSwitch(null); }}
            size={44}
          />
          {/* Family members */}
          {familyProfiles
            .filter(p => p.relation_type?.toLowerCase() !== 'self' && p.relation_type?.toLowerCase() !== 'client')
            .map((profile) => {
              const isActive = activeProfile?.profile_id === profile.profile_id;
              const color = RELATION_COLORS[profile.relation_type] || RELATION_COLORS.Other;
              const labelColor = RELATION_LABEL_COLORS[profile.relation_type] || 'text-amber-200';
              return (
                <Avatar
                  key={profile.profile_id}
                  label={profile.name?.split(' ')[0] || 'Member'}
                  gradient={color}
                  isActive={isActive}
                  isSelf={false}
                  editMode={editMode}
                  labelColorClass={labelColor}
                  onClick={() => {
                    if (editMode) {
                      openEditPanel(profile);
                    } else {
                      onSwitch(profile);
                    }
                  }}
                  onDelete={() => handleDeleteMember(profile.profile_id)}
                  size={44}
                />
              );
            })}
          {/* Add / Edit mode toggle button */}
          {!editMode ? (
            <motion.button
              whileTap={{ scale: 0.94 }}
              onClick={isLocked ? undefined : () => { setShowAddPanel(v => !v); setEditingProfile(null); }}
              className="flex flex-col items-center gap-1.5 flex-shrink-0 focus:outline-none"
              data-testid="add-family-profile-btn"
            >
              <div className={`w-[44px] h-[44px] rounded-full border-2 border-dashed flex items-center justify-center transition-colors ${
                showAddPanel ? 'border-amber-400/60 bg-amber-500/10' : 'border-amber-500/20 hover:border-amber-400/40'
              }`}>
                {isLocked ? <Lock className="w-4 h-4 text-gray-600" />
                  : showAddPanel ? <X className="w-4 h-4 text-amber-400" />
                  : <Plus className="w-4 h-4 text-amber-500/60" />}
              </div>
              <span className="text-[9px] text-amber-500/50">{showAddPanel ? 'Cancel' : 'Add'}</span>
            </motion.button>
          ) : (
            <motion.button
              whileTap={{ scale: 0.94 }}
              onClick={closeEditMode}
              className="flex flex-col items-center gap-1.5 flex-shrink-0 focus:outline-none"
              data-testid="exit-edit-mode-btn"
            >
              <div className="w-[44px] h-[44px] rounded-full border-2 border-emerald-500/40 bg-emerald-500/10 flex items-center justify-center">
                <Check className="w-4 h-4 text-emerald-400" />
              </div>
              <span className="text-[9px] text-emerald-400/70">Done</span>
            </motion.button>
          )}

          {/* Edit mode toggle (pencil) */}
          {familyProfiles.filter(p => p.relation_type?.toLowerCase() !== 'self' && p.relation_type?.toLowerCase() !== 'client').length > 0 && !showAddPanel && (
            <motion.button
              whileTap={{ scale: 0.94 }}
              onClick={() => { setEditMode(v => !v); setEditingProfile(null); setShowAddPanel(false); }}
              className="flex flex-col items-center gap-1.5 flex-shrink-0 focus:outline-none"
              data-testid="edit-mode-toggle-btn"
            >
              <div className={`w-[44px] h-[44px] rounded-full border-2 flex items-center justify-center transition-colors ${
                editMode ? 'border-amber-400/60 bg-amber-500/10' : 'border-white/10 hover:border-amber-500/30'
              }`}>
                <Pencil className={`w-3.5 h-3.5 ${editMode ? 'text-amber-400' : 'text-white/30'}`} />
              </div>
              <span className={`text-[9px] ${editMode ? 'text-amber-400/70' : 'text-white/25'}`}>Edit</span>
            </motion.button>
          )}
        </div>

        {/* Active profile indicator */}
        <AnimatePresence>
          {activeProfile && !editMode && (
            <motion.div
              key="strip-active-info"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2 }}
              className="overflow-hidden"
            >
              <div className="flex items-center gap-2 px-4 pt-1.5 pb-1">
                <p className="text-[10px] text-gray-400 leading-none">
                  Reading for <span className="text-amber-400 font-semibold">{activeProfile.name}</span>
                  <span className="text-gray-600"> · {activeProfile.relation_type}</span>
                </p>
                <motion.button
                  whileTap={{ scale: 0.95 }}
                  onClick={() => onSwitch(null)}
                  className="ml-auto text-[9px] text-amber-400/70 font-medium"
                  data-testid="switch-to-self-btn"
                >
                  ← Me
                </motion.button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Inline Add / Edit Member Panel */}
        <AnimatePresence>
          {(showAddPanel || editingProfile) && (
            <motion.div
              key={editingProfile ? `edit-${editingProfile.profile_id}` : 'add-panel'}
              initial={{ opacity: 0, height: 0, marginTop: 0 }}
              animate={{ opacity: 1, height: 'auto', marginTop: 8 }}
              exit={{ opacity: 0, height: 0, marginTop: 0 }}
              transition={{ duration: 0.22 }}
              className="overflow-hidden mx-4"
              data-testid={editingProfile ? 'edit-member-panel' : 'add-member-inline-panel'}
            >
              <FamilyMemberForm
                editingProfile={editingProfile}
                onSave={(profile) => {
                  if (editingProfile) {
                    onUpdate?.(profile);
                    setEditingProfile(null);
                  } else {
                    onAdd?.(profile);
                    setShowAddPanel(false);
                  }
                }}
                onCancel={() => {
                  setShowAddPanel(false);
                  setEditingProfile(null);
                }}
              />
            </motion.div>
          )}
        </AnimatePresence>

      </div>
    );
  }

  // ── Full card mode (desktop / right column) ──────────────────────────────
  return (
    <div
      className={`relative overflow-hidden backdrop-blur-[14px] rounded-xl border transition-all duration-200 ${
        activeProfile
          ? 'border-amber-400/30 shadow-[0_0_18px_rgba(251,191,36,0.08)]'
          : 'border-[rgba(201,168,76,0.25)]'
      } ${compact ? 'p-3' : 'p-3.5'}`}
      style={{ background: 'rgba(255,255,255,0.06)' }}
      data-testid="profile-switcher-container"
    >
      {/* ── Subtle star dust — matches other cards ── */}
      <svg aria-hidden style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none', zIndex: 0 }}>
        {PS_STARS.map((s, i) => (
          <circle
            key={i} cx={s.cx} cy={s.cy} r={s.r}
            fill={s.fill}
            opacity={+(s.opacity * 0.45).toFixed(2)}
            style={s.twinkle ? { animation: `cs-tw ${s.dur} ease-in-out infinite alternate` } : undefined}
          />
        ))}
      </svg>

      {/* ── All content above the cosmic layers ── */}
      <div className="relative z-10">

      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <span className="text-[9px] font-semibold tracking-[0.12em] uppercase" style={{ color: '#F59E0B' }}>
          Profiles
        </span>
        <div className="flex items-center gap-2">
          {familyProfiles.filter(p => p.relation_type?.toLowerCase() !== 'self' && p.relation_type?.toLowerCase() !== 'client').length > 0 && (
            <motion.button
              whileTap={{ scale: 0.9 }}
              onClick={() => { setEditMode(v => !v); setEditingProfile(null); setShowAddPanel(false); }}
              className={`flex items-center gap-1 text-[9px] font-medium transition-colors px-1.5 py-0.5 rounded-md ${
                editMode ? 'text-amber-400 bg-amber-500/10 border border-amber-500/30' : 'text-gray-500 hover:text-amber-400/70'
              }`}
              data-testid="desktop-edit-mode-toggle"
            >
              <Pencil className="w-2.5 h-2.5" /> {editMode ? 'Done' : 'Edit'}
            </motion.button>
          )}
          {activeProfile && !editMode && (
            <motion.button
              initial={{ opacity: 0, x: 6 }}
              animate={{ opacity: 1, x: 0 }}
              onClick={() => onSwitch(null)}
              className="flex items-center gap-1 text-[9px] text-amber-400/80 hover:text-amber-300 font-medium transition-colors"
              data-testid="switch-to-self-btn"
            >
              <X className="w-3 h-3" />
              Back to Me
            </motion.button>
          )}
        </div>
      </div>

      {/* Scrollable row */}
      <div className="relative">
        {showLeft && (
          <button
            onClick={() => scroll(-1)}
            className="absolute left-0 top-1/2 -translate-y-4 z-10 w-5 h-8 bg-[rgba(26,5,51,0.8)] flex items-center justify-center rounded-r-md"
          >
            <ChevronLeft className="w-3 h-3 text-gray-400" />
          </button>
        )}

        <div
          ref={scrollRef}
          onScroll={checkScroll}
          className="flex gap-3.5 overflow-x-auto pb-1 scrollbar-none"
          style={{ scrollbarWidth: 'none' }}
        >
          {/* Me */}
          <Avatar
            label="Me"
            gradient={isSelf ? 'from-amber-400 to-orange-500' : selfGradient}
            isActive={isSelf}
            isSelf
            onClick={() => onSwitch(null)}
          />

          {/* Family members — exclude 'self' relation (primary user already shown as Me) */}
          {familyProfiles
            .filter(p => p.relation_type?.toLowerCase() !== 'self' && p.relation_type?.toLowerCase() !== 'client')
            .map((profile) => {
            const isActive = activeProfile?.profile_id === profile.profile_id;
            const color = RELATION_COLORS[profile.relation_type] || RELATION_COLORS.Other;
            const labelColor = RELATION_LABEL_COLORS[profile.relation_type] || 'text-amber-200';
            return (
              <Avatar
                key={profile.profile_id}
                label={profile.name?.split(' ')[0] || 'Member'}
                gradient={color}
                isActive={isActive}
                isSelf={false}
                editMode={editMode}
                labelColorClass={labelColor}
                onClick={() => {
                  if (editMode) { openEditPanel(profile); }
                  else { onSwitch(profile); }
                }}
                onDelete={() => handleDeleteMember(profile.profile_id)}
              />
            );
          })}

          {/* Add button */}
          {!editMode && (
          <motion.button
            whileHover={{ scale: 1.06 }}
            whileTap={{ scale: 0.94 }}
            onClick={isLocked ? undefined : () => { setShowAddPanel(v => !v); setEditingProfile(null); }}
            className="flex flex-col items-center gap-1.5 flex-shrink-0 focus:outline-none"
            data-testid="add-family-profile-btn"
          >
            <div className={`w-[48px] h-[48px] rounded-full border-2 border-dashed flex items-center justify-center transition-colors ${
              showAddPanel ? 'border-amber-400/60 bg-amber-500/10' : 'border-amber-500/20 hover:border-amber-400/40'
            }`}>
              {isLocked ? <Lock className="w-4 h-4 text-gray-600" />
                : showAddPanel ? <X className="w-4 h-4 text-amber-400" />
                : <Plus className="w-4 h-4 text-amber-500/50" />}
            </div>
            <span className="text-[9px] text-amber-500/40">{isLocked ? 'Locked' : showAddPanel ? 'Cancel' : 'Add'}</span>
          </motion.button>
          )}
        </div>

        {showRight && (
          <button
            onClick={() => scroll(1)}
            className="absolute right-0 top-1/2 -translate-y-4 z-10 w-5 h-8 bg-[rgba(26,5,51,0.8)] flex items-center justify-center rounded-l-md"
          >
            <ChevronRight className="w-3 h-3 text-gray-400" />
          </button>
        )}
      </div>

      {/* Active profile info */}
      <AnimatePresence>
        {activeProfile && !editMode && (
          <motion.div
            key="active-info"
            initial={{ opacity: 0, height: 0, marginTop: 0 }}
            animate={{ opacity: 1, height: 'auto', marginTop: 10 }}
            exit={{ opacity: 0, height: 0, marginTop: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="pt-2.5 border-t border-white/8">
              <p className="text-[10px] text-gray-400 leading-relaxed">
                Reading for{' '}
                <span className="text-amber-400 font-semibold">{activeProfile.name}</span>
                <span className="text-gray-600">
                  {' '}· {activeProfile.relation_type} · {activeProfile.dob}
                </span>
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Inline Add / Edit Member Panel ── */}
      <AnimatePresence>
        {(showAddPanel || editingProfile) && (
          <motion.div
            key={editingProfile ? `edit-${editingProfile.profile_id}` : 'add-panel'}
            initial={{ opacity: 0, height: 0, marginTop: 0 }}
            animate={{ opacity: 1, height: 'auto', marginTop: 12 }}
            exit={{ opacity: 0, height: 0, marginTop: 0 }}
            transition={{ duration: 0.22 }}
            className="overflow-hidden"
            data-testid={editingProfile ? 'edit-member-panel' : 'add-member-inline-panel'}
          >
            <div className="pt-3 border-t border-white/10">
              <FamilyMemberForm
                editingProfile={editingProfile}
                onSave={(profile) => {
                  if (editingProfile) {
                    onUpdate?.(profile);
                    setEditingProfile(null);
                  } else {
                    onAdd?.(profile);
                    setShowAddPanel(false);
                  }
                }}
                onCancel={() => {
                  setShowAddPanel(false);
                  setEditingProfile(null);
                }}
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      </div>{/* end relative z-10 */}
    </div>
  );
};

export default ProfileSwitcher;
